import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle, Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react'
import { WifiIcon, InformationCircleIcon, ArrowLeftIcon, CalendarIcon, ChevronDownIcon, PlusIcon, MinusIcon } from '@heroicons/react/24/solid'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { BoatBookingImage, BoatIcon } from 'components/icons'
import { t } from 'i18next'
import stSeat from '../../../assets/images/icons/st_seat.svg'
import vipSeat from '../../../assets/images/icons/vip_seat.svg'
import { customDateFormat, customTimeFormat, getTimeDifference } from 'utils/common'
import { BookingPassengersForm } from '../components/passengersForm'
import { BookingSeatSelection } from '../components/seatSelectLayout'
import { AddOns } from '../components/add_ons'
import { useMutation, useLazyQuery } from '@apollo/client'
import { CREATE_BOOKING } from 'graphql/booking/mutations'
import { GET_MEAL_DETAILS_FROM_TRIP, GET_ADD_ON_PRICE_CONFIG } from 'graphql/common/queries'
import moment from 'moment'

import { ErrorDialog } from 'components/custom/ResponseDialog'
import BoatBookingConformation from '../confirmation'

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

const BoatBookingDetail = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { tripDetails, departureTrip, filterObj, isRoundTrip } = location.state || {}
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [dialogMessage, setDialogMessage] = useState('')
    const [submitLoading, setSubmitLoading] = useState(false)
    const [showConfirmBookingDialogue, setShowConfirmBookingDialogue] = useState(false)
    const [activeTab, setActiveTab] = useState(0)
    const [bookingInfo, setBookingInfo] = useState(null)
    const [open, setOpen] = useState(true)
    const [passengersDetails, setPassengersDetails] = useState({})
    const [totalPrice, setTotalPrice] = useState(0)
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [bookingData, setBookingData] = useState(null)
    // Helper function to determine if trip is international
    const isInternationalTrip = () => {
        return filterObj?.form?.routeType === 'INL' // INL is international, DMC is domestic
    }

    const [enableTab, setEnableTab] = useState({
        passengerInfo: true,
        seatSelection: false,
        addons: isInternationalTrip() ? false : false, // Only enable for international trips
        confirmDetails: false,
    })

    // Addon state variables
    const [selectedAddons, setSelectedAddons] = useState({
        departure: {
            meals: {},
            luggage: {
                '0-8kg': false,
                '9-15kg': false,
                '16-25kg': false,
                '26-40kg': false,
            },
            special_items: {
                stroller: 0,
                wheelchair: 0
            },
            shuttle_service: {
                selected: false,
                route_shuttle_id: null,
                provider_details: null
            }
        },
        return: {
            meals: {},
            luggage: {
                '0-8kg': false,
                '9-15kg': false,
                '16-25kg': false,
                '26-40kg': false,
            },
            special_items: {
                stroller: 0,
                wheelchair: 0
            },
            shuttle_service: {
                selected: false,
                route_shuttle_id: null,
                provider_details: null
            }
        }
    })
    
    // Dynamic meals from API
    const [tripMeals, setTripMeals] = useState({
        departure: [],
        return: []
    })
    
    // Add-on price configuration from API
    const [addonPriceConfig, setAddonPriceConfig] = useState({
        extra_luggage: [],
        special_items: {
            stroller_price: 0,
            wheelchair_price: 0
        }
    })
    
    // Legacy addon prices for luggage (meals will come from API)
    const [addonPrices] = useState({
        luggage: {
            '0-8kg': 3,
            '9-15kg': 6,
            '16-25kg': 10,
            '26-40kg': 15,
        }
    })
    
    // Accordion state for addon sections
    const [openAddonSection, setOpenAddonSection] = useState('meals') // 'meals' or 'luggage'
    
    // New state for sequential addon display
    const [addonStep, setAddonStep] = useState('departure') // 'departure' or 'return'
    const [departureAddonsSelected, setDepartureAddonsSelected] = useState(false)

    const handleTabClick = index => {
        setActiveTab(index)
    }
    

    const handleNavigation = () => {
        navigate('/boat-booking', { state: { filterObj } })
    }
    const confirmBooking = () => {
        setShowConfirmBookingDialogue(false)
        setSubmitLoading(true)
        
        // Helper to compute DOB for domestic trips based on age group
        const computeDobForDomestic = (ageGroupCode) => {
            const now = new Date()
            const date = new Date(now)
            switch (ageGroupCode) {
                case 'SNR':
                    date.setFullYear(now.getFullYear() - 70)
                    break
                case 'ADL':
                    date.setFullYear(now.getFullYear() - 30)
                    break
                case 'CHD':
                    date.setFullYear(now.getFullYear() - 5)
                    break
                case 'INF':
                    date.setFullYear(now.getFullYear() - 1)
                    break
                default:
                    date.setFullYear(now.getFullYear() - 30)
            }
            return moment(date).format('YYYY-MM-DD')
        }

        // Get DOB to send in payload depending on trip type
        const getDobForPayload = (passengerInfo) => {
            if (isInternationalTrip()) {
                return moment(passengerInfo.passenger.date_of_birth, 'DD/MM/YYYY').format('YYYY-MM-DD')
            }
            return computeDobForDomestic(passengerInfo.seat_info.age_group_code)
        }

        // Helper function to prepare document array for passenger
        const prepareDocumentArray = (passengerInfo) => {
            if (!isInternationalTrip()) {
                return null // No documents for domestic trips
            }

            const documents = []
            
            // Helper to check if object is a valid S3 file structure
            const isValidS3File = (file) => {
                return file && file.s3_key && file.file_name && file.mime_type && file.size
            }
            
            // Add passport attachment if exists and is valid S3 file
            if (passengerInfo.passenger.passport_attachment && isValidS3File(passengerInfo.passenger.passport_attachment)) {
                documents.push({
                    s3_key: passengerInfo.passenger.passport_attachment.s3_key,
                    file_name: passengerInfo.passenger.passport_attachment.file_name,
                    mime_type: passengerInfo.passenger.passport_attachment.mime_type,
                    size: passengerInfo.passenger.passport_attachment.size
                })
            }
            
            // Add visa attachment if exists and is valid S3 file
            if (passengerInfo.passenger.visa_attachment && isValidS3File(passengerInfo.passenger.visa_attachment)) {
                documents.push({
                    s3_key: passengerInfo.passenger.visa_attachment.s3_key,
                    file_name: passengerInfo.passenger.visa_attachment.file_name,
                    mime_type: passengerInfo.passenger.visa_attachment.mime_type,
                    size: passengerInfo.passenger.visa_attachment.size
                })
            }
            
            // Add legacy document if exists and is valid S3 file (for backward compatibility)
            if (passengerInfo.passenger.document && isValidS3File(passengerInfo.passenger.document)) {
                documents.push({
                    s3_key: passengerInfo.passenger.document.s3_key,
                    file_name: passengerInfo.passenger.document.file_name,
                    mime_type: passengerInfo.passenger.document.mime_type,
                    size: passengerInfo.passenger.document.size
                })
            }
            
            return documents.length > 0 ? documents : null
        }

        // Helper function to prepare meals for a trip segment
        const prepareMealsForSegment = (tripType) => {
            if (!passengersDetails.addons?.selected?.[tripType]?.meals) {
                return []
            }
            
            const meals = []
            Object.entries(passengersDetails.addons.selected[tripType].meals).forEach(([tripMealId, quantity]) => {
                if (quantity > 0) {
                    // Find the meal details to get the meal_id
                    const mealDetails = tripMeals[tripType].find(meal => meal.trip_meal_id === tripMealId)
                    if (mealDetails) {
                        meals.push({
                            meal_id: mealDetails.meal_id,
                            meal_quantity: quantity
                        })
                    }
                }
            })
            return meals
        }
        
        // Prepare trip segments based on trip type
        const tripSegments = []
        
        if (isRoundTrip && departureTrip) {
            // Add departure trip segment
            tripSegments.push({
                segment_id: null,
                segment_order: 1,
                travel_date: filterObj.form?.searchDate,
                route_id: departureTrip.route?.route_id,
                trip_id: departureTrip.trip?.trip_id,
                trip_location: {
                    start_location_id: filterObj.form?.fromLocationId,
                    end_location_id: filterObj.form?.toLocationId,
                },
                passengers: passengersDetails.passengers.map((info, index) => {
                    const dob = getDobForPayload(info)
                    let email = index === 0 ? passengersDetails.email : null
                    let mobile = index === 0 ? passengersDetails.phoneNumber : null
                    const documents = prepareDocumentArray(info)
                    return {
                            passenger: {
                                given_name: info.passenger.given_name,
                                family_name: info.passenger.family_name,
                                date_of_birth: dob,
                                email: email,
                                phone_number: mobile,
                                document_type: isInternationalTrip() ? (info.passenger.passport_number ? 'PPR' : null) : null,
                                document_number: info.passenger.passport_number || info.passenger.document_number || null,
                                age_group_code: info.seat_info.age_group_code,
                                document: documents,
                                nationality_code: info.passenger.nationality_code || null,
                                special_needs: isInternationalTrip() ? Boolean(info.passenger.special_needs) : false,
                                medical_condition: Boolean(info.passenger.medical_condition),
                                medical_remarks: info.passenger.medical_condition ? info.passenger.medical_remarks : null,
                            },
                        seat_info: {
                            seat_no: info.seat_info.departure_seat_no || info.seat_info.seat_no,
                            seat_type_code: info.seat_info.departure_seat_type_code || info.seat_info.seat_type_code,
                        },
                    }
                }),
                meals: prepareMealsForSegment('departure'),
                shuttle_service_add_on: passengersDetails.addons?.selected?.departure?.shuttle_service?.selected || false
            })
            
            // Add return trip segment
            tripSegments.push({
                segment_id: null,
                segment_order: 2,
                travel_date: filterObj.form?.returnDate,
                route_id: tripDetails.route?.route_id,
                trip_id: tripDetails.trip?.trip_id,
                trip_location: {
                    start_location_id: filterObj.form?.toLocationId,
                    end_location_id: filterObj.form?.fromLocationId,
                },
                passengers: passengersDetails.passengers.map((info, index) => {
                    const dob = getDobForPayload(info)
                    let email = index === 0 ? passengersDetails.email : null
                    let mobile = index === 0 ? passengersDetails.phoneNumber : null
                    const documents = prepareDocumentArray(info)
                    return {
                            passenger: {
                                given_name: info.passenger.given_name,
                                family_name: info.passenger.family_name,
                                date_of_birth: dob,
                                email: email,
                                phone_number: mobile,
                                document_type: isInternationalTrip() ? (info.passenger.passport_number ? 'PPR' : null) : null,
                                document_number: info.passenger.passport_number || info.passenger.document_number || null,
                                age_group_code: info.seat_info.age_group_code,
                                document: documents,
                                nationality_code: info.passenger.nationality_code || null,
                                special_needs: isInternationalTrip() ? Boolean(info.passenger.special_needs) : false,
                                medical_condition: Boolean(info.passenger.medical_condition),
                                medical_remarks: info.passenger.medical_condition ? info.passenger.medical_remarks : null,
                            },
                        seat_info: {
                            seat_no: info.return_seat_info?.seat_no || info.seat_info.seat_no,
                            seat_type_code: info.return_seat_info?.seat_type_code || info.seat_info.seat_type_code,
                        },
                    }
                }),
                meals: prepareMealsForSegment('return'),
                shuttle_service_add_on: passengersDetails.addons?.selected?.return?.shuttle_service?.selected || false
            })
        } else {
            // One-way trip
            tripSegments.push({
                segment_id: null,
                segment_order: 1,
                travel_date: filterObj.form?.searchDate,
                route_id: tripDetails.route?.route_id,
                trip_id: tripDetails.trip?.trip_id,
                trip_location: {
                    start_location_id: filterObj.form?.fromLocationId,
                    end_location_id: filterObj.form?.toLocationId,
                },
                passengers: passengersDetails.passengers.map((info, index) => {
                    const dob = getDobForPayload(info)
                    let email = index === 0 ? passengersDetails.email : null
                    let mobile = index === 0 ? passengersDetails.phoneNumber : null
                    const documents = prepareDocumentArray(info)
                    return {
                            passenger: {
                                given_name: info.passenger.given_name,
                                family_name: info.passenger.family_name,
                                date_of_birth: dob,
                                email: email,
                                phone_number: mobile,
                                document_type: isInternationalTrip() ? (info.passenger.passport_number ? 'PPR' : null) : null,
                                document_number: info.passenger.passport_number || info.passenger.document_number || null,
                                age_group_code: info.seat_info.age_group_code,
                                document: documents,
                                nationality_code: info.passenger.nationality_code || null,
                                special_needs: isInternationalTrip() ? Boolean(info.passenger.special_needs) : false,
                                medical_condition: Boolean(info.passenger.medical_condition),
                                medical_remarks: info.passenger.medical_condition ? info.passenger.medical_remarks : null,
                            },
                        seat_info: {
                            seat_no: info.seat_info.seat_no,
                            seat_type_code: info.seat_info.seat_type_code,
                        },
                    }
                }),
                meals: prepareMealsForSegment('departure'),
                shuttle_service_add_on: passengersDetails.addons?.selected?.departure?.shuttle_service?.selected || false
            })
        }
        
        // Generate a unique device ID for this session
        const generateDeviceId = () => {
            const existingDeviceId = localStorage.getItem('device_id')
            if (existingDeviceId) {
                return existingDeviceId
            }
            const newDeviceId = 'device_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
            localStorage.setItem('device_id', newDeviceId)
            return newDeviceId
        }

        const bookingData = {
            bookingId: null,
            booking: {
                trip_segments: tripSegments,
                metadata: {
                    device_id: generateDeviceId(),
                    booking_id: null,
                    metadata_prefered_current_type: "BHD" // Default currency - can be made configurable later
                }
            },
        }
        
        confirmNewBooking({ variables: bookingData })
    }
    const handleErrClose = () => {
        setShowErrDialog(false)
        setDialogMessage('')
    }

    // Query for fetching trip meals
    const [getTripMeals, { loading: mealsLoading }] = useLazyQuery(GET_MEAL_DETAILS_FROM_TRIP, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            if (data?.getMealDetailsFromTripByTripIdForPublicApp?.success) {
                const meals = data.getMealDetailsFromTripByTripIdForPublicApp.trip_meals
                    .filter(meal => meal.meal_detail.active)
                    .map(meal => ({
                        trip_meal_id: meal.trip_meal_id,
                        meal_id: meal.meal_detail.meal_id,
                        description: meal.meal_detail.description,
                        price: meal.meal_detail.price,
                        totalPrice: meal.meal_detail.price.base + meal.meal_detail.price.tax
                    }))
                
                // Determine if this is departure or return trip based on the trip ID
                const responseTripId = data.getMealDetailsFromTripByTripIdForPublicApp.trip_meals[0]?.trip_id
                
                // For one-way trips, always use 'departure' since there's only one trip
                // For round trips, check if this matches the departure trip ID
                let tripType = 'departure' // Default for one-way trips
                if (isRoundTrip && departureTrip) {
                    const isDepartureTrip = responseTripId === departureTrip.trip?.trip_id
                    tripType = isDepartureTrip ? 'departure' : 'return'
                }
                
                
                // Initialize meal selections for all active meals
                const mealSelections = {}
                meals.forEach(meal => {
                    mealSelections[meal.trip_meal_id] = 0 // Initialize with quantity 0, not false
                })
                
                setTripMeals(prev => ({
                    ...prev,
                    [tripType]: meals
                }))
                
                // Update selectedAddons with new meal structure
                setSelectedAddons(prev => ({
                    ...prev,
                    [tripType]: {
                        ...prev[tripType],
                        meals: mealSelections
                    }
                }))
            }
        },
        onError: (error) => {
            console.error('Error fetching trip meals:', error)
        }
    })

    // Query for fetching add-on price configuration
    const [getAddOnPriceConfig, { loading: addonConfigLoading }] = useLazyQuery(GET_ADD_ON_PRICE_CONFIG, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            if (data?.getAddOnPriceConfig?.success) {
                const addonDetails = data.getAddOnPriceConfig.add_on_details
                setAddonPriceConfig({
                    extra_luggage: addonDetails.extra_luggage || [],
                    special_items: addonDetails.special_items?.[0] || {
                        stroller_price: 0,
                        wheelchair_price: 0
                    }
                })
            }
        },
        onError: (error) => {
            console.error('Error fetching add-on price config:', error)
        }
    })

    const [confirmNewBooking, { loading: confirmLoading }] = useMutation(CREATE_BOOKING, {
        onCompleted: data => {
            if (data?.createBooking.code === '200' && data?.createBooking.success === true) {
                setSubmitLoading(false)
                if (data?.createBooking.booking.booking_id) {
                    // New API structure - booking is created successfully
                    setBookingData(data.createBooking.booking)
                    setIsDialogOpen(true);
                }
            } else if (data?.createBooking.code === 'SEAT_NOT_AVAILABLE') {
                setSubmitLoading(false)
                setShowErrDialog(true)
                setDialogMessage(data?.createBooking.message)
                handleSeatNotAvailable()
            } else {
                setSubmitLoading(false)
                setShowErrDialog(true)
                setDialogMessage(data?.createBooking.message || 'Booking creation failed')
            }
        },
        onError: error => {
            setSubmitLoading(false)
            setDialogMessage(error.message)
            setShowErrDialog(true)
        },
    })

    const handleSeatNotAvailable = () => {
        handleTabClick(1)
        setEnableTab({
            passengerInfo: true,
            seatSelection: true,
            addons: isInternationalTrip() ? false : false,
            confirmDetails: false,
        })
        // remove seat_price from passengersDetails.passengers.seat_info.seat_price
        const passengers = passengersDetails.passengers.map(passenger => {
            const updatedPassenger = {
                ...passenger,
                seat_info: {
                    ...passenger.seat_info,
                    seat_no: '', // Set seat_no to empty
                    seat_price: '', // Set seat_price to empty
                    seat_type_code: '', // Set seat_type_code to empty
                },
            }
            return updatedPassenger
        })
        setPassengersDetails({
            ...passengersDetails,
            passengers: passengers,
        })
        
        // Reset addon selections
        setSelectedAddons({
            departure: {
                meals: {},
                luggage: {
                    '0-8kg': false,
                    '9-15kg': false,
                    '16-25kg': false,
                    '26-40kg': false,
                },
                special_items: {
                    stroller: 0,
                    wheelchair: 0
                }
            },
            return: {
                meals: {},
                luggage: {
                    '0-8kg': false,
                    '9-15kg': false,
                    '16-25kg': false,
                    '26-40kg': false,
                },
                special_items: {
                    stroller: 0,
                    wheelchair: 0
                }
            }
        })
        
        // Reset trip meals
        setTripMeals({
            departure: [],
            return: []
        })
        
        // Reset addon step for round trips
        if (isRoundTrip && departureTrip) {
            setAddonStep('departure')
            setDepartureAddonsSelected(false)
        }
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button className="inline-flex items-center gap-x-1.5 rounded-md text-sm" onClick={handleNavigation}>
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5" />
                    {t('Cancel')}
                </button>
            </div>
            {!tripDetails && (
                <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                    <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="mx-auto h-12 w-12 text-gray-400"
                    >
                        <path
                            d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                            strokeWidth={2}
                            vectorEffect="non-scaling-stroke"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('Problem fetch details!')}</h3>
                    <p className="mt-1 text-sm text-gray-500">{t('Please adjusting your search criteria')}</p>
                    <div className="mt-6"></div>
                </div>
            )}
            {tripDetails && (
                <>
                    <div></div>
                    <div className="flex flex-col lg:flex-row gap-6">
                        <div className="w-full lg:w-1/3 xl:w-1/4 p-4 lg:p-6 bg-white shadow-lg border-solid border-2 border-gray-200 rounded-lg h-max">
                            <div className="mb-4">
                                <h2 className="text-base font-semibold leading-7 text-gray-900">
                                    {t('Route Info')} :
                                </h2>
                            </div>
                            {isRoundTrip && departureTrip ? (
                                <>
                                    {/* Departure Route Info */}
                                    <Disclosure defaultOpen={true}>
                                        {({ open }) => (
                                            <>
                                                <DisclosureButton className="flex w-full justify-between items-center py-2 text-left text-sm font-medium focus:outline-none focus-visible:ring focus-visible:ring-primary focus-visible:ring-opacity-75">
                                                    <div className="flex items-center space-x-2">
                                                        <BoatBookingImage className="w-[50px] h-[50px] fill-[#285f74]" />
                                                        <div className="flex flex-col">
                                                            <span className="text-xl font-bold text-left">{departureTrip.trip?.trip_name?.en}</span>
                                                            <span className="text-sm text-gray-600">{t('Departure Route')}</span>
                                                        </div>
                                                    </div>
                                                    <ChevronDownIcon
                                                        className={`${
                                                            open ? 'transform rotate-180' : ''
                                                        } w-5 h-5 text-gray-500`}
                                                    />
                                                </DisclosureButton>
                                                <DisclosurePanel className="px-4 pb-2 pt-4 text-sm text-gray-500">
                                                    <div className="mb-4">
                                                        <div className="mb-3">
                                                            <div className="text-sm text-blue-600 font-medium mb-1">
                                                                {departureTrip.trip_date}
                                                            </div>
                                                        </div>
                                                        <div className="flow-root">
                                                            {/* Mobile: Simple trip details */}
                                                            <div className="block md:hidden">
                                                                <div className="space-y-4">
                                                                    {/* Start location */}
                                                                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                                                                        <p className="text-sm font-medium text-blue-800">From</p>
                                                                        <p className="text-base font-semibold text-gray-900">
                                                                            {departureTrip.start.location?.name?.en}
                                                                        </p>
                                                                        <p className="text-xs text-gray-600 mt-1">
                                                                                        {customTimeFormat(departureTrip?.start?.departure_time)}
                                                                        </p>
                                                                    </div>
                                                                    
                                                                    {/* Middle stops info */}
                                                                    {departureTrip?.middle_stops && departureTrip?.middle_stops?.length > 0 && (
                                                                        <div className="text-center p-2 bg-gray-50 rounded-lg">
                                                                            <p className="text-xs text-gray-600">
                                                                                {departureTrip?.middle_stops?.length} intermediate stops
                                                                            </p>
                                                                        </div>
                                                                    )}
                                                                    
                                                                    {/* End location */}
                                                                    <div className="text-center p-3 bg-green-50 rounded-lg">
                                                                        <p className="text-sm font-medium text-green-800">To</p>
                                                                        <p className="text-base font-semibold text-gray-900">
                                                                            {departureTrip.end.location?.name?.en}
                                                                        </p>
                                                                        <p className="text-xs text-gray-600 mt-1">
                                                                            {customTimeFormat(departureTrip?.end?.arrival_time)}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            
                                                            {/* Desktop: Horizontal timeline */}
                                                            <ul className="hidden md:block -mb-8">
                                                                <li key={'start'}>
                                                                    <div className="relative pb-4">
                                                                        <span
                                                                            aria-hidden="true"
                                                                            className="absolute left-2 top-4 -ml-px h-full w-0.5 bg-gray-200"
                                                                        />
                                                                        <div className="relative flex space-x-3">
                                                                            <div>
                                                                                <span
                                                                                    className={classNames(
                                                                                        'flex h-4 w-4 items-center justify-center rounded-full ring-8 ring-white border-solid border-2 border-primary'
                                                                                    )}
                                                                                ></span>
                                                                            </div>
                                                                            <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-0 border-solid border-b-2 border-gray-100 pb-4">
                                                                                <div>
                                                                                    <p className="text-md text-gray-500 font-medium text-gray-900">
                                                                                        {departureTrip.start.location?.name?.en}
                                                                                    </p>
                                                                                    <p className="flex text-sm text-gray-300 font-medium text-gray-900 pt-2">
                                                                                        <InformationCircleIcon
                                                                                            aria-hidden="true"
                                                                                            className="h-5 w-5 text-primary font-[12px] me-1"
                                                                                        />
                                                                                        {customTimeFormat(departureTrip?.start?.departure_time)}
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </li>
                                                                {departureTrip?.middle_stops?.map((tripSchedule, eventIdx) => (
                                                                    <li key={tripSchedule?.stop_number}>
                                                                        <div className="relative pb-4">
                                                                            <span
                                                                                aria-hidden="true"
                                                                                className="absolute left-2 top-4 -ml-px h-full w-0.5 bg-gray-200"
                                                                            />
                                                                            <div className="relative flex space-x-3">
                                                                                <div>
                                                                                    <span
                                                                                        className={classNames(
                                                                                            tripSchedule.iconBackground,
                                                                                            'flex h-4 w-4 items-center justify-center rounded-full ring-8 ring-white border-solid border-2 border-primary'
                                                                                        )}
                                                                                    ></span>
                                                                                </div>
                                                                                <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-0 border-solid border-b-2 border-gray-100 pb-4">
                                                                                    <div>
                                                                                        <p className="text-md text-gray-500 font-medium text-gray-900">
                                                                                            {tripSchedule.location?.name?.en}
                                                                                        </p>
                                                                                        <p className="flex text-sm text-gray-300 font-medium text-gray-900 pt-2">
                                                                                            <InformationCircleIcon
                                                                                                aria-hidden="true"
                                                                                                className="h-5 w-5 text-primary font-[12px] me-1"
                                                                                            />
                                                                                            {customTimeFormat(tripSchedule.departure_time)}
                                                                                        </p>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </li>
                                                                ))}
                                                                <li key={100}>
                                                                    <div className="relative pb-4">
                                                                        <div className="relative flex space-x-3">
                                                                            <div>
                                                                                <span
                                                                                    className={classNames(
                                                                                        'flex h-4 w-4 items-center justify-center rounded-full ring-8 ring-white border-solid border-2 border-primary'
                                                                                    )}
                                                                                ></span>
                                                                            </div>
                                                                            <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-0 border-solid border-b-2 border-gray-100 pb-4">
                                                                                <div>
                                                                                    <p className="text-md text-gray-500 font-medium text-gray-900">
                                                                                        {departureTrip.end.location?.name?.en}
                                                                                    </p>
                                                                                    <p className="flex text-sm text-gray-300 font-medium text-gray-900 pt-2">
                                                                                        <InformationCircleIcon
                                                                                            aria-hidden="true"
                                                                                            className="h-5 w-5 text-primary font-[12px] me-1"
                                                                                        />
                                                                                        {customTimeFormat(departureTrip?.end?.arrival_time)}
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </DisclosurePanel>
                                            </>
                                        )}
                                    </Disclosure>

                                    {/* Return Route Info */}
                                    <Disclosure defaultOpen={false}>
                                        {({ open }) => (
                                            <>
                                                <DisclosureButton className="flex w-full justify-between items-center py-2 text-left text-sm font-medium focus:outline-none focus-visible:ring focus-visible:ring-primary focus-visible:ring-opacity-75">
                                                    <div className="flex items-center space-x-2">
                                                        <BoatBookingImage className="w-[50px] h-[50px] fill-[#285f74]" />
                                                        <div className="flex flex-col">
                                                            <span className="text-xl font-bold text-left">{tripDetails.trip?.trip_name?.en}</span>
                                                            <span className="text-sm text-gray-600">{t('Return Route')}</span>
                                                        </div>
                                                    </div>
                                                    <ChevronDownIcon
                                                        className={`${
                                                            open ? 'transform rotate-180' : ''
                                                        } w-5 h-5 text-gray-500`}
                                                    />
                                                </DisclosureButton>
                                                <DisclosurePanel className="px-4 pb-2 pt-4 text-sm text-gray-500">
                                                    <div className="mb-4">
                                                        <div className="mb-3">
                                                            <div className="text-sm text-green-600 font-medium mb-1">
                                                                {tripDetails.trip_date}
                                                            </div>
                                                        </div>
                                                        <div className="flow-root">
                                                            {/* Mobile: Simple trip details */}
                                                            <div className="block md:hidden">
                                                                <div className="space-y-4">
                                                                    {/* Start location */}
                                                                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                                                                        <p className="text-sm font-medium text-blue-800">From</p>
                                                                        <p className="text-base font-semibold text-gray-900">
                                                                            {tripDetails?.start?.location?.name?.en}
                                                                        </p>
                                                                        <p className="text-xs text-gray-600 mt-1">
                                                                            {customTimeFormat(tripDetails?.start?.departure_time)}
                                                                        </p>
                                                                    </div>
                                                                    
                                                                    {/* Middle stops info */}
                                                                    {tripDetails?.middle_stops && tripDetails?.middle_stops?.length > 0 && (
                                                                        <div className="text-center p-2 bg-gray-50 rounded-lg">
                                                                            <p className="text-xs text-gray-600">
                                                                                {tripDetails?.middle_stops?.length} intermediate stops
                                                                            </p>
                                                                        </div>
                                                                    )}
                                                                    
                                                                    {/* End location */}
                                                                    <div className="text-center p-3 bg-green-50 rounded-lg">
                                                                        <p className="text-sm font-medium text-green-800">To</p>
                                                                        <p className="text-base font-semibold text-gray-900">
                                                                            {tripDetails.end.location?.name?.en}
                                                                        </p>
                                                                        <p className="text-xs text-gray-600 mt-1">
                                                                            {customTimeFormat(tripDetails?.end?.arrival_time)}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            
                                                            {/* Desktop: Horizontal timeline */}
                                                            <ul className="hidden md:block -mb-8">
                                                                <li key={'start'}>
                                                                    <div className="relative pb-4">
                                                                        <span
                                                                            aria-hidden="true"
                                                                            className="absolute left-2 top-4 -ml-px h-full w-0.5 bg-gray-200"
                                                                        />
                                                                        <div className="relative flex space-x-3">
                                                                            <div>
                                                                                <span
                                                                                    className={classNames(
                                                                                        'flex h-4 w-4 items-center justify-center rounded-full ring-8 ring-white border-solid border-2 border-primary'
                                                                                    )}
                                                                                ></span>
                                                                            </div>
                                                                            <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-0 border-solid border-b-2 border-gray-100 pb-4">
                                                                                <div>
                                                                                    <p className="text-md text-gray-500 font-medium text-gray-900">
                                                                                        {tripDetails.start.location?.name?.en}
                                                                                    </p>
                                                                                    <p className="flex text-sm text-gray-300 font-medium text-gray-900 pt-2">
                                                                                        <InformationCircleIcon
                                                                                            aria-hidden="true"
                                                                                            className="h-5 w-5 text-primary font-[12px] me-1"
                                                                                        />
                                                                                        {customTimeFormat(tripDetails?.start?.departure_time)}
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </li>
                                                                {tripDetails.middle_stops?.map((tripSchedule, eventIdx) => (
                                                                    <li key={tripSchedule?.stop_number}>
                                                                        <div className="relative pb-4">
                                                                            <span
                                                                                aria-hidden="true"
                                                                                className="absolute left-2 top-4 -ml-px h-full w-0.5 bg-gray-200"
                                                                            />
                                                                            <div className="relative flex space-x-3">
                                                                                <div>
                                                                                    <span
                                                                                        className={classNames(
                                                                                            tripSchedule.iconBackground,
                                                                                            'flex h-4 w-4 items-center justify-center rounded-full ring-8 ring-white border-solid border-2 border-primary'
                                                                                        )}
                                                                                    ></span>
                                                                                </div>
                                                                                <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-0 border-solid border-b-2 border-gray-100 pb-4">
                                                                                    <div>
                                                                                        <p className="text-md text-gray-500 font-medium text-gray-900">
                                                                                            {tripSchedule.location?.name?.en}
                                                                                        </p>
                                                                                        <p className="flex text-sm text-gray-300 font-medium text-gray-900 pt-2">
                                                                                            <InformationCircleIcon
                                                                                                aria-hidden="true"
                                                                                                className="h-5 w-5 text-primary font-[12px] me-1"
                                                                                            />
                                                                                            {customTimeFormat(tripSchedule.departure_time)}
                                                                                        </p>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </li>
                                                                ))}
                                                                <li key={100}>
                                                                    <div className="relative pb-4">
                                                                        <div className="relative flex space-x-3">
                                                                            <div>
                                                                                <span
                                                                                    className={classNames(
                                                                                        'flex h-4 w-4 items-center justify-center rounded-full ring-8 ring-white border-solid border-2 border-primary'
                                                                                    )}
                                                                                ></span>
                                                                            </div>
                                                                            <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-0 border-solid border-b-2 border-gray-100 pb-4">
                                                                                <div>
                                                                                    <p className="text-md text-gray-500 font-medium text-gray-900">
                                                                                        {tripDetails.end.location?.name?.en}
                                                                                    </p>
                                                                                    <p className="flex text-sm text-gray-300 font-medium text-gray-900 pt-2">
                                                                                        <InformationCircleIcon
                                                                                            aria-hidden="true"
                                                                                            className="h-5 w-5 text-primary font-[12px] me-1"
                                                                                        />
                                                                                        {customTimeFormat(tripDetails?.end?.arrival_time)}
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </DisclosurePanel>
                                            </>
                                        )}
                                    </Disclosure>
                                </>
                            ) : (
                                <>
                                    {/* Single Trip Route Info */}
                                    <Disclosure defaultOpen={true}>
                                        {({ open }) => (
                                            <>
                                                <DisclosureButton className="flex w-full justify-between items-center py-2 text-left text-sm font-medium focus:outline-none focus-visible:ring focus-visible:ring-primary focus-visible:ring-opacity-75">
                                                    <div className="flex items-center space-x-2">
                                                        <BoatBookingImage className="w-[50px] h-[50px] fill-[#285f74]" />
                                                        <div className="flex flex-col">
                                                            <span className="text-xl font-bold text-left">{tripDetails.trip?.trip_name?.en}</span>
                                                            <span className="text-sm text-gray-600">{t('Route Info')}</span>
                                                        </div>
                                                    </div>
                                                    <ChevronDownIcon
                                                        className={`${
                                                            open ? 'transform rotate-180' : ''
                                                        } w-5 h-5 text-gray-500`}
                                                    />
                                                </DisclosureButton>
                                                <DisclosurePanel className="px-4 pb-2 pt-4 text-sm text-gray-500">
                                                    <div className="mb-4">
                                                        <div className="mb-3">
                                                            <div className="text-sm text-gray-600 font-medium mb-1">
                                                                {getTimeDifference(
                                                                    tripDetails?.trip?.start_time,
                                                                    tripDetails?.trip?.end_time
                                                                )}{' '}
                                                                | {tripDetails?.middle_stops?.length} stops
                                                            </div>
                                                        </div>
                                                        <div className="flow-root">
                                                            {/* Mobile: Simple trip details */}
                                                            <div className="block md:hidden">
                                                                <div className="space-y-4">
                                                                    {/* Start location */}
                                                                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                                                                        <p className="text-sm font-medium text-blue-800">From</p>
                                                                        <p className="text-base font-semibold text-gray-900">
                                                                            {tripDetails?.start?.location?.name?.en}
                                                                        </p>
                                                                        <p className="text-xs text-gray-600 mt-1">
                                                                            {customTimeFormat(tripDetails?.start?.departure_time)}
                                                                        </p>
                                                                    </div>
                                                                    
                                                                    {/* Middle stops info */}
                                                                    {tripDetails?.middle_stops && tripDetails?.middle_stops?.length > 0 && (
                                                                        <div className="text-center p-2 bg-gray-50 rounded-lg">
                                                                            <p className="text-xs text-gray-600">
                                                                                {tripDetails?.middle_stops?.length} intermediate stops
                                                                            </p>
                                                                        </div>
                                                                    )}
                                                                    
                                                                    {/* End location */}
                                                                    <div className="text-center p-3 bg-green-50 rounded-lg">
                                                                        <p className="text-sm font-medium text-green-800">To</p>
                                                                        <p className="text-base font-semibold text-gray-900">
                                                                            {tripDetails.end.location?.name?.en}
                                                                        </p>
                                                                        <p className="text-xs text-gray-600 mt-1">
                                                                            {customTimeFormat(tripDetails?.end?.arrival_time)}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            
                                                            {/* Desktop: Horizontal timeline */}
                                                            <ul className="hidden md:block -mb-8">
                                                                <li key={'start'}>
                                                                    <div className="relative pb-4">
                                                                        <span
                                                                            aria-hidden="true"
                                                                            className="absolute left-2 top-4 -ml-px h-full w-0.5 bg-gray-200"
                                                                        />
                                                                        <div className="relative flex space-x-3">
                                                                            <div>
                                                                                <span
                                                                                    className={classNames(
                                                                                        'flex h-4 w-4 items-center justify-center rounded-full ring-8 ring-white border-solid border-2 border-primary'
                                                                                    )}
                                                                                ></span>
                                                                            </div>
                                                                            <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-0 border-solid border-b-2 border-gray-100 pb-4">
                                                                                <div>
                                                                                    <p className="text-md text-gray-500 font-medium text-gray-900">
                                                                                        {tripDetails.start.location?.name?.en}
                                                                                    </p>
                                                                                    <p className="flex text-sm text-gray-300 font-medium text-gray-900 pt-2">
                                                                                        <InformationCircleIcon
                                                                                            aria-hidden="true"
                                                                                            className="h-5 w-5 text-primary font-[12px] me-1"
                                                                                        />
                                                                                        {customTimeFormat(tripDetails?.start?.departure_time)}
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </li>
                                                                {tripDetails?.middle_stops?.map((tripSchedule, eventIdx) => (
                                                                    <li key={tripSchedule?.stop_number}>
                                                                        <div className="relative pb-4">
                                                                            <span
                                                                                aria-hidden="true"
                                                                                className="absolute left-2 top-4 -ml-px h-full w-0.5 bg-gray-200"
                                                                            />
                                                                            <div className="relative flex space-x-3">
                                                                                <div>
                                                                                    <span
                                                                                        className={classNames(
                                                                                            tripSchedule.iconBackground,
                                                                                            'flex h-4 w-4 items-center justify-center rounded-full ring-8 ring-white border-solid border-2 border-primary'
                                                                                        )}
                                                                                    ></span>
                                                                                </div>
                                                                                <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-0 border-solid border-b-2 border-gray-100 pb-4">
                                                                                    <div>
                                                                                        <p className="text-md text-gray-500 font-medium text-gray-900">
                                                                                            {tripSchedule.location?.name?.en}
                                                                                        </p>
                                                                                        <p className="flex text-sm text-gray-300 font-medium text-gray-900 pt-2">
                                                                                            <InformationCircleIcon
                                                                                                aria-hidden="true"
                                                                                                className="h-5 w-5 text-primary font-[12px] me-1"
                                                                                            />
                                                                                            {customTimeFormat(tripSchedule.departure_time)}
                                                                                        </p>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </li>
                                                                ))}
                                                                <li key={100}>
                                                                    <div className="relative pb-4">
                                                                        <div className="relative flex space-x-3">
                                                                            <div>
                                                                                <span
                                                                                    className={classNames(
                                                                                        'flex h-4 w-4 items-center justify-center rounded-full ring-8 ring-white border-solid border-2 border-primary'
                                                                                    )}
                                                                                ></span>
                                                                            </div>
                                                                            <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-0 border-solid border-b-2 border-gray-100 pb-4">
                                                                                <div>
                                                                                    <p className="text-md text-gray-500 font-medium text-gray-900">
                                                                                        {tripDetails.end.location?.name?.en}
                                                                                    </p>
                                                                                    <p className="flex text-sm text-gray-300 font-medium text-gray-900 pt-2">
                                                                                        <InformationCircleIcon
                                                                                            aria-hidden="true"
                                                                                            className="h-5 w-5 text-primary font-[12px] me-1"
                                                                                        />
                                                                                        {customTimeFormat(tripDetails?.end?.arrival_time)}
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </DisclosurePanel>
                                            </>
                                        )}
                                    </Disclosure>
                                </>
                            )}
                        </div>
                        <div className="w-full lg:w-2/3 xl:w-3/4">
                            <div className="flex justify-between">
                                <div className="flex flex-col flex-wrap items-start gap-2 sm:flex-nowrap">
                                    <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                                        <span className="">{t('Fill the below information')}</span>
                                    </h1>
                                </div>
                                <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap">
                                    {/* <button className="px-6 py-2 bg-teal-600 text-white rounded">Next</button> */}
                                </div>
                            </div>
                            <div className="max-w-4xl mx-auto pt-2">
                                {/* Tabs */}
                                <div className="border-b mb-4 overflow-x-auto">
                                    <div className="flex min-w-max">
                                        <button
                                            className={`py-3 px-3 sm:px-6 text-sm sm:text-base whitespace-nowrap ${activeTab === 0
                                                ? 'border-b-2 border-teal-600 text-teal-600'
                                                : 'text-gray-500 hover:text-gray-700'
                                                }`}
                                            onClick={() => handleTabClick(0)}
                                        >
                                            {t('Passenger Info')}
                                        </button>
                                        <button
                                            className={`py-3 px-3 sm:px-6 text-sm sm:text-base whitespace-nowrap ${activeTab === 1
                                                ? 'border-b-2 border-teal-600 text-teal-600'
                                                : 'text-gray-500 hover:text-gray-700'
                                                } ${!enableTab.seatSelection ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            onClick={() => {
                                                if (enableTab.seatSelection) {
                                                    handleTabClick(1)
                                                }
                                            }}
                                            disabled={!enableTab.seatSelection}
                                        >
                                            {t('Seat Selection')}
                                        </button>
                                        {isInternationalTrip() && (
                                            <button
                                                className={`py-3 px-3 sm:px-6 text-sm sm:text-base whitespace-nowrap ${activeTab === 2
                                                    ? 'border-b-2 border-teal-600 text-teal-600'
                                                    : 'text-gray-500 hover:text-gray-700'
                                                    } ${!enableTab.addons ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                onClick={() => {
                                                    if (enableTab.addons) {
                                                        handleTabClick(2)
                                                    }
                                                }}
                                                disabled={!enableTab.addons}
                                            >
                                                {t('Addons & Upgrades')}
                                            </button>
                                        )}
                                        <button
                                            className={`py-3 px-3 sm:px-6 text-sm sm:text-base whitespace-nowrap ${activeTab === 3
                                                ? 'border-b-2 border-teal-600 text-teal-600'
                                                : 'text-gray-500 hover:text-gray-700'
                                                } ${!enableTab.confirmDetails ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            onClick={() => {
                                                if (enableTab.confirmDetails) {
                                                    handleTabClick(3)
                                                }
                                            }}
                                            disabled={!enableTab.confirmDetails}
                                        >
                                            {t('Confirm Details')}
                                        </button>
                                    </div>
                                </div>

                                {/* Tab Content */}
                                {activeTab === 0 &&
                                    enableTab.passengerInfo && (
                                        <BookingPassengersForm
                                            personInfo={filterObj.person}
                                            initialPassengerDetails={passengersDetails}
                                            routeType={filterObj.form?.routeType}
                                            onAddPassenger={values => {
                                                setPassengersDetails(values)
                                                if (values?.passengers?.length > 0) {
                                                    setEnableTab({
                                                        passengerInfo: true,
                                                        seatSelection: true,
                                                        addons: isInternationalTrip() ? false : false,
                                                        confirmDetails: false,
                                                    })
                                                    setActiveTab(1)
                                                }
                                            }}
                                        />
                                    )}

                                {activeTab === 1 && enableTab.seatSelection && (
                                    <BookingSeatSelection
                                        tripDetails={{
                                            routeId: tripDetails.route_id,
                                            tripId: tripDetails.trip?.trip_id,
                                            startLocation: isRoundTrip ? filterObj.form?.toLocationId : filterObj.form?.fromLocationId,
                                            endLocation: isRoundTrip ? filterObj.form?.fromLocationId : filterObj.form?.toLocationId,
                                            departureDate: isRoundTrip ? filterObj.form?.returnDate : filterObj.form?.searchDate,
                                            isRoundTrip: isRoundTrip,
                                            isInternational: isInternationalTrip(),
                                            departureTrip: {
                                                ...departureTrip,
                                                start_location_id: filterObj.form?.fromLocationId,
                                                end_location_id: filterObj.form?.toLocationId,
                                            },
                                            returnTrip: isRoundTrip ? {
                                                ...tripDetails,
                                                trip_date: filterObj.form?.returnDate,
                                                start_location_id: filterObj.form?.toLocationId,
                                                end_location_id: filterObj.form?.fromLocationId,
                                            } : null,
                                        }}
                                        passengerDetails={passengersDetails}
                                        priceDetails={tripDetails.price}
                                        availableSeatTypeCount={tripDetails.available_seats}
                                        onSeatSelection={passengersWithPrice => {
                                            
                                            // For round trips, we need to properly map departure and return seat info from the API response
                                            if (isRoundTrip && departureTrip) {
                                                // The API response contains seat info for both departure and return trips
                                                // We need to extract and map them correctly
                                                const updatedPassengers = passengersWithPrice.passengers.map((passenger, index) => {
                                                    // Find departure and return seat info from the API response
                                                    const departureSeatInfo = passenger.seat_info
                                                    const returnSeatInfo = passenger.return_seat_info
                                                    
                                                    return {
                                                        ...passenger,
                                                        seat_info: {
                                                            ...departureSeatInfo,
                                                            // Ensure departure seat info is preserved
                                                            departure_seat_no: departureSeatInfo.seat_no,
                                                            departure_seat_type_code: departureSeatInfo.seat_type_code,
                                                            departure_seat_price: departureSeatInfo.seat_price,
                                                            // Keep the original seat_price for backward compatibility
                                                            seat_price: departureSeatInfo.seat_price,
                                                        },
                                                        return_seat_info: {
                                                            seat_no: returnSeatInfo?.seat_no || departureSeatInfo.seat_no,
                                                            seat_type_code: returnSeatInfo?.seat_type_code || departureSeatInfo.seat_type_code,
                                                            seat_price: returnSeatInfo?.seat_price || departureSeatInfo.seat_price,
                                                        }
                                                    }
                                                })
                                                
                                                setPassengersDetails({
                                                    ...passengersDetails,
                                                    passengers: updatedPassengers,
                                                    priceBreakdown: passengersWithPrice.priceBreakdown,
                                                })
                                            } else {
                                                // One-way trip
                                                setPassengersDetails({
                                                    ...passengersDetails,
                                                    passengers: passengersWithPrice.passengers,
                                                    priceBreakdown: passengersWithPrice.priceBreakdown,
                                                })
                                            }
                                            
                                            setTotalPrice(passengersWithPrice.totalCost)
                                            if (passengersWithPrice.totalCost > 0) {
                                                // For international trips, fetch meals and enable addons tab
                                                if (isInternationalTrip()) {
                                                    // Fetch meals for departure trip only initially
                                                    if (filterObj.form?.searchDate) {
                                                        // For round trips, use departureTrip.trip_id, for one-way use tripDetails.trip_id
                                                        const tripId = isRoundTrip && departureTrip 
                                                            ? departureTrip.trip?.trip_id 
                                                            : tripDetails.trip?.trip_id
                                                        
                                                        if (tripId) {
                                                            getTripMeals({
                                                                variables: {
                                                                    tripId: tripId,
                                                                    travelDate: filterObj.form.searchDate
                                                                }
                                                            })
                                                            
                                                            // Fetch add-on price configuration
                                                            getAddOnPriceConfig({
                                                                variables: {
                                                                    routeId: isRoundTrip && departureTrip 
                                                                        ? departureTrip.route?.route_id 
                                                                        : tripDetails.route?.route_id,
                                                                    tripId: tripId,
                                                                    tripDate: filterObj.form.searchDate
                                                                }
                                                            })
                                                        }
                                                    }
                                                    
                                                    // Reset addon step to departure for round trips
                                                    if (isRoundTrip && departureTrip) {
                                                        setAddonStep('departure')
                                                        setDepartureAddonsSelected(false)
                                                    }
                                                    
                                                    setEnableTab({
                                                        passengerInfo: true,
                                                        seatSelection: true,
                                                        addons: true,
                                                        confirmDetails: false,
                                                    })
                                                    setActiveTab(2)
                                                } else {
                                                    // For domestic trips, skip addons and go directly to confirm details
                                                    setEnableTab({
                                                        passengerInfo: true,
                                                        seatSelection: true,
                                                        addons: false,
                                                        confirmDetails: true,
                                                    })
                                                    setActiveTab(3)
                                                }
                                            }
                                        }}
                                    />
                                )}

                                {activeTab === 2 && enableTab.addons && isInternationalTrip() && (
                                    <AddOns
                                        isRoundTrip={isRoundTrip}
                                        departureTrip={departureTrip}
                                        tripDetails={tripDetails}
                                        filterObj={filterObj}
                                        tripMeals={tripMeals}
                                        setTripMeals={setTripMeals}
                                        selectedAddons={selectedAddons}
                                        setSelectedAddons={setSelectedAddons}
                                        addonPrices={addonPrices}
                                        addonPriceConfig={addonPriceConfig}
                                        setAddonPriceConfig={setAddonPriceConfig}
                                        openAddonSection={openAddonSection}
                                        setOpenAddonSection={setOpenAddonSection}
                                        addonStep={addonStep}
                                        setAddonStep={setAddonStep}
                                        departureAddonsSelected={departureAddonsSelected}
                                        setDepartureAddonsSelected={setDepartureAddonsSelected}
                                        mealsLoading={mealsLoading}
                                        addonConfigLoading={addonConfigLoading}
                                        getTripMeals={getTripMeals}
                                        getAddOnPriceConfig={getAddOnPriceConfig}
                                        passengersDetails={passengersDetails}
                                        setPassengersDetails={setPassengersDetails}
                                        totalPrice={totalPrice}
                                        setTotalPrice={setTotalPrice}
                                        setEnableTab={setEnableTab}
                                        setActiveTab={setActiveTab}
                                    />
                                )}

                                {activeTab === 3 &&
                                    enableTab.confirmDetails &&
                                    passengersDetails.passengers?.length > 0 && (
                                            <div className="max-w mx-auto bg-white rounded-lg shadow-lg border-[1px] border-solid border-[#D6D6D6] p-6 mt-5 mb-5">
                                                <div className="text-center mb-6">
                                                    <h2 className="text-xl font-bold">
                                                        {isRoundTrip && departureTrip 
                                                            ? `${t('Round Trip')}: ${departureTrip.trip?.trip_name?.en} & ${tripDetails.trip?.trip_name?.en}`
                                                            : tripDetails.trip?.trip_name?.en
                                                        }
                                                    </h2>
                                                    {isRoundTrip && departureTrip && (
                                                        <p className="text-sm text-gray-600 mt-2">
                                                            {t('Departure')}: {departureTrip.trip_date} | {t('Return')}: {tripDetails.trip_date}
                                                        </p>
                                                    )}
                                                </div>

                                                {/* Route Info with Passenger Details */}
                                                <div className="bg-[#F4F7F8] rounded-sm p-3 md:p-4 mb-4">
                                                    {isRoundTrip && departureTrip ? (
                                                        <>
                                                            {/* Departure Trip Info */}
                                                            <div className="mb-3 p-3 bg-white rounded-lg border border-gray-200">
                                                                <h3 className="text-base font-bold text-gray-900 mb-2">{t('Departure Trip')}</h3>
                                                                
                                                                {/* Trip Route Info */}
                                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-2 mb-3 pb-2 border-b border-gray-200">
                                                                    <div className="flex items-center gap-2">
                                                                        <BoatIcon className="w-4 h-4 fill-[#285f74] flex-shrink-0" />
                                                                        <div className="text-start min-w-0">
                                                                            <p className="text-[11px] text-gray-500">{t('From Location')}</p>
                                                                            <p className="font-semibold text-sm text-gray-900 truncate">{departureTrip.start?.location?.name?.en}</p>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <BoatIcon className="w-4 h-4 fill-[#285f74] flex-shrink-0" />
                                                                        <div className="text-start min-w-0">
                                                                            <p className="text-[11px] text-gray-500">{t('To Location')}</p>
                                                                            <p className="font-semibold text-sm text-gray-900 truncate">{departureTrip.end?.location?.name?.en}</p>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <CalendarIcon className="w-4 h-4 text-[#285f74] flex-shrink-0" />
                                                                        <div className="text-start min-w-0">
                                                                            <p className="text-[11px] text-gray-500">{t('Departure Date')}</p>
                                                                            <p className="font-semibold text-sm text-gray-900 truncate">
                                                                                {customDateFormat(filterObj.form?.searchDate)} - {customTimeFormat(departureTrip.trip?.start_time)}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Departure Passengers */}
                                                                <div>
                                                                    <h4 className="text-xs font-semibold text-gray-600 mb-2">{t('Passengers')}</h4>
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                                        {passengersDetails?.passengers?.map((info, index) => (
                                                                            <div key={`dep-${index}`} className="flex items-center gap-2 bg-gray-50 px-2 py-1.5 rounded">
                                                                                <p className="font-medium text-sm text-gray-900 flex-1 min-w-0 truncate">
                                                                                    {info.passenger.given_name} {info.passenger.family_name}
                                                                                </p>
                                                                                <div className="flex items-center gap-1 bg-yellow-50 px-1.5 py-0.5 rounded">
                                                                                    <img
                                                                                        src={info.seat_info.departure_seat_type_code === 'VIP' ? vipSeat : stSeat}
                                                                                        className="w-3 h-3"
                                                                                        alt="seat"
                                                                                    />
                                                                                    <span className="text-[11px] font-medium text-gray-700">{info.seat_info.departure_seat_type_code}</span>
                                                                                </div>
                                                                                <div className="flex items-center gap-1 bg-gray-200 px-1.5 py-0.5 rounded">
                                                                                    <span className="block w-2.5 h-2.5 rounded-sm bg-gray-500"></span>
                                                                                    <span className="text-[11px] font-medium text-gray-700">{info.seat_info.departure_seat_no}</span>
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            
                                                            {/* Return Trip Info */}
                                                            <div className="p-3 bg-white rounded-lg border border-gray-200">
                                                                <h3 className="text-base font-bold text-gray-900 mb-2">{t('Return Trip')}</h3>
                                                                
                                                                {/* Trip Route Info */}
                                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-2 mb-3 pb-2 border-b border-gray-200">
                                                                    <div className="flex items-center gap-2">
                                                                        <BoatIcon className="w-4 h-4 fill-[#285f74] flex-shrink-0" />
                                                                        <div className="text-start min-w-0">
                                                                            <p className="text-[11px] text-gray-500">{t('From Location')}</p>
                                                                            <p className="font-semibold text-sm text-gray-900 truncate">{tripDetails.start?.location?.name?.en}</p>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <BoatIcon className="w-4 h-4 fill-[#285f74] flex-shrink-0" />
                                                                        <div className="text-start min-w-0">
                                                                            <p className="text-[11px] text-gray-500">{t('To Location')}</p>
                                                                            <p className="font-semibold text-sm text-gray-900 truncate">{tripDetails.end?.location?.name?.en}</p>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <CalendarIcon className="w-4 h-4 text-[#285f74] flex-shrink-0" />
                                                                        <div className="text-start min-w-0">
                                                                            <p className="text-[11px] text-gray-500">{t('Return Date')}</p>
                                                                            <p className="font-semibold text-sm text-gray-900 truncate">
                                                                                {customDateFormat(filterObj.form?.returnDate)} - {customTimeFormat(tripDetails.trip?.start_time)}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Return Passengers */}
                                                                <div>
                                                                    <h4 className="text-xs font-semibold text-gray-600 mb-2">{t('Passengers')}</h4>
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                                        {passengersDetails?.passengers?.map((info, index) => (
                                                                            <div key={`ret-${index}`} className="flex items-center gap-2 bg-gray-50 px-2 py-1.5 rounded">
                                                                                <p className="font-medium text-sm text-gray-900 flex-1 min-w-0 truncate">
                                                                                    {info.passenger.given_name} {info.passenger.family_name}
                                                                                </p>
                                                                                <div className="flex items-center gap-1 bg-yellow-50 px-1.5 py-0.5 rounded">
                                                                                    <img
                                                                                        src={info.return_seat_info?.seat_type_code === 'VIP' ? vipSeat : stSeat}
                                                                                        className="w-3 h-3"
                                                                                        alt="seat"
                                                                                    />
                                                                                    <span className="text-[11px] font-medium text-gray-700">{info.return_seat_info?.seat_type_code}</span>
                                                                                </div>
                                                                                <div className="flex items-center gap-1 bg-gray-200 px-1.5 py-0.5 rounded">
                                                                                    <span className="block w-2.5 h-2.5 rounded-sm bg-gray-500"></span>
                                                                                    <span className="text-[11px] font-medium text-gray-700">{info.return_seat_info?.seat_no}</span>
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <div className="p-3 bg-white rounded-lg border border-gray-200">
                                                            {/* One-way Trip Route Info */}
                                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-2 mb-3 pb-2 border-b border-gray-200">
                                                                <div className="flex items-center gap-2">
                                                                    <BoatIcon className="w-4 h-4 fill-[#285f74] flex-shrink-0" />
                                                                    <div className="text-start min-w-0">
                                                                        <p className="text-[11px] text-gray-500">{t('From Location')}</p>
                                                                        <p className="font-semibold text-sm text-gray-900 truncate">{tripDetails.start?.location?.name?.en}</p>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <BoatIcon className="w-4 h-4 fill-[#285f74] flex-shrink-0" />
                                                                    <div className="text-start min-w-0">
                                                                        <p className="text-[11px] text-gray-500">{t('To Location')}</p>
                                                                        <p className="font-semibold text-sm text-gray-900 truncate">{tripDetails.end?.location?.name?.en}</p>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <CalendarIcon className="w-4 h-4 text-[#285f74] flex-shrink-0" />
                                                                    <div className="text-start min-w-0">
                                                                        <p className="text-[11px] text-gray-500">{t('Departure Date')}</p>
                                                                        <p className="font-semibold text-sm text-gray-900 truncate">
                                                                            {customDateFormat(filterObj.form?.searchDate)} - {customTimeFormat(tripDetails.start?.departure_time)}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* One-way Passengers */}
                                                            <div>
                                                                <h4 className="text-xs font-semibold text-gray-600 mb-2">{t('Passengers')}</h4>
                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                                    {passengersDetails?.passengers?.map((info, index) => (
                                                                        <div key={`oneway-${index}`} className="flex items-center gap-2 bg-gray-50 px-2 py-1.5 rounded">
                                                                            <p className="font-medium text-sm text-gray-900 flex-1 min-w-0 truncate">
                                                                                {info.passenger.given_name} {info.passenger.family_name}
                                                                            </p>
                                                                            <div className="flex items-center gap-1 bg-yellow-50 px-1.5 py-0.5 rounded">
                                                                                <img
                                                                                    src={info.seat_info.seat_type_code === 'VIP' ? vipSeat : stSeat}
                                                                                    className="w-3 h-3"
                                                                                    alt="seat"
                                                                                />
                                                                                <span className="text-[11px] font-medium text-gray-700">{info.seat_info.seat_type_code}</span>
                                                                            </div>
                                                                            <div className="flex items-center gap-1 bg-gray-200 px-1.5 py-0.5 rounded">
                                                                                <span className="block w-2.5 h-2.5 rounded-sm bg-gray-500"></span>
                                                                                <span className="text-[11px] font-medium text-gray-700">{info.seat_info.seat_no}</span>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Payment Summary Section */}
                                                <div className="border-gray-300 pt-1 pb-2">
                                                    {passengersDetails.priceBreakdown && (
                                                        <div className="bg-white border border-gray-200 rounded-lg mb-4">
                                                            {/* Header */}
                                                            <div className="px-4 py-3 border-b border-gray-200">
                                                                <h4 className="text-base font-bold text-gray-900">{t('Payment Summary')}</h4>
                                                            </div>
                                                            
                                                            {/* Items List */}
                                                            <div className="px-4 py-3 space-y-2">
                                                                {/* Price Summary from API - For International Trips */}
                                                                {passengersDetails.priceBreakdown.price_summary && passengersDetails.priceBreakdown.price_summary.length > 0 ? (
                                                                    passengersDetails.priceBreakdown.price_summary.map((item, index) => (
                                                                        <div key={index} className="flex items-center justify-between py-1.5">
                                                                            <div className="flex items-center gap-2.5">
                                                                                <span className="text-gray-500 text-sm">{item.unit_no}x</span>
                                                                                <div className="flex items-center gap-1.5">
                                                                                    <img
                                                                                        src={item.price_type === 'seat' && (item.type?.en === 'VIP' || item.type?.ar === 'VIP') ? vipSeat : stSeat}
                                                                                        className="w-4 h-4"
                                                                                        alt="seat"
                                                                                    />
                                                                                    <span className="text-gray-700 text-sm font-medium">{item.type?.en || item.type?.ar}</span>
                                                                                </div>
                                                                            </div>
                                                                            <span className="text-gray-900 font-semibold text-sm">{item.total_price?.toFixed(2)} {passengersDetails.priceBreakdown.currency || 'BHD'}</span>
                                                                        </div>
                                                                    ))
                                                                ) : (
                                                                    /* Domestic Trip Ticket Breakdown */
                                                                    <div className="flex items-center justify-between py-1.5">
                                                                        <span className="text-gray-700 text-sm font-medium">{t('Ticket Price')}</span>
                                                                        <span className="text-gray-900 font-semibold text-sm">
                                                                            {passengersDetails.priceBreakdown.totalPriceBeforeDiscount?.toFixed(2)} {passengersDetails.priceBreakdown.currency || 'BHD'}
                                                                        </span>
                                                                    </div>
                                                                )}

                                                                {/* Display shuttle service from API response */}
                                                                {passengersDetails.priceBreakdown.shuttle_service && passengersDetails.priceBreakdown.shuttle_service.length > 0 && (
                                                                    passengersDetails.priceBreakdown.shuttle_service.map((service, index) => (
                                                                        <div key={`api-shuttle-${index}`} className="flex items-center justify-between py-1.5">
                                                                            <div className="flex items-center gap-2.5">
                                                                                <span className="text-gray-500 text-sm">1x</span>
                                                                                <span className="text-gray-700 text-sm font-medium">{t('Bus Shuttle Services')}</span>
                                                                            </div>
                                                                            <span className="text-gray-900 font-semibold text-sm">{service.price.price_with_tax?.toFixed(2)} {passengersDetails.priceBreakdown.currency || 'BHD'}</span>
                                                                        </div>
                                                                    ))
                                                                )}
                                                                
                                                                {/* Display meals from API response */}
                                                                {passengersDetails.priceBreakdown.meals && passengersDetails.priceBreakdown.meals.length > 0 && (
                                                                    passengersDetails.priceBreakdown.meals.map((meal, index) => (
                                                                        <div key={`api-meal-${index}`} className="flex items-center justify-between py-1.5">
                                                                            <div className="flex items-center gap-2.5">
                                                                                <span className="text-gray-500 text-sm">{meal.quantity}x</span>
                                                                                <span className="text-gray-700 text-sm font-medium">{meal.name_in_english || meal.name_in_arabic || 'Meal'}</span>
                                                                            </div>
                                                                            <span className="text-gray-900 font-semibold text-sm">{meal.price.price_with_tax?.toFixed(2)} {passengersDetails.priceBreakdown.currency || 'BHD'}</span>
                                                                        </div>
                                                                    ))
                                                                )}
                                                                
                                                                {/* Display extra luggage from API response */}
                                                                {passengersDetails.priceBreakdown.extra_luggage && passengersDetails.priceBreakdown.extra_luggage.length > 0 && (
                                                                    passengersDetails.priceBreakdown.extra_luggage.map((luggage, index) => (
                                                                        <div key={`api-luggage-${index}`} className="flex items-center justify-between py-1.5">
                                                                            <div className="flex items-center gap-2.5">
                                                                                <span className="text-gray-500 text-sm">{luggage.quantity}x</span>
                                                                                <span className="text-gray-700 text-sm font-medium">{luggage.weight_kg}kg Extra Luggage</span>
                                                                            </div>
                                                                            <span className="text-gray-900 font-semibold text-sm">{luggage.price.price_with_tax?.toFixed(2)} {passengersDetails.priceBreakdown.currency || 'BHD'}</span>
                                                                        </div>
                                                                    ))
                                                                )}
                                                                
                                                                {/* Display special items from API response */}
                                                                {passengersDetails.priceBreakdown.special_items && passengersDetails.priceBreakdown.special_items.length > 0 && (
                                                                    passengersDetails.priceBreakdown.special_items.map((item, index) => (
                                                                        <div key={`api-special-${index}`} className="flex items-center justify-between py-1.5">
                                                                            <div className="flex items-center gap-2.5">
                                                                                <span className="text-gray-500 text-sm">{item.quantity}x</span>
                                                                                <span className="text-gray-700 text-sm font-medium">{item.item === 'stroller' ? t('Stroller') : t('Wheelchair')}</span>
                                                                            </div>
                                                                            <span className="text-gray-900 font-semibold text-sm">{item.price.price_with_tax?.toFixed(2)} {passengersDetails.priceBreakdown.currency || 'BHD'}</span>
                                                                        </div>
                                                                    ))
                                                                )}
                                                            </div>
                                                            
                                                            {/* Total Section */}
                                                            <div className="px-4 py-3 border-t border-gray-200">
                                                                <div className="flex items-center justify-between">
                                                                    <span className="text-base font-bold text-gray-900">{t('Total')}</span>
                                                                    <span className="text-lg font-bold text-gray-900">{totalPrice?.toFixed(2)} {passengersDetails.priceBreakdown?.currency || 'BHD'}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                {/* Action Button */}
                                {activeTab === 3 && enableTab.confirmDetails && (
                                    <div className="flex justify-end bg-primary/[.06] p-4 rounded-md">
                                        <button
                                            onClick={() => setShowConfirmBookingDialogue(true)}
                                            type="submit"
                                            className="px-6 py-2 bg-teal-600 text-white rounded"
                                        >
                                            {t('Confirm Booking')}
                                        </button>
                                    </div>
                                )}
                                <Dialog
                                    open={showConfirmBookingDialogue}
                                    onClose={setShowConfirmBookingDialogue}
                                    className="relative z-10"
                                >
                                    <DialogBackdrop
                                        transition
                                        className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
                                    />

                                    <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                                        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                                            <DialogPanel
                                                transition
                                                className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                                            >
                                                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                                    <div className="sm:flex sm:items-start">
                                                        <div
                                                            className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10 bg-red-800"
                                                            style={{ backgroundColor: '#ff00002e' }}
                                                        >
                                                            <ExclamationTriangleIcon
                                                                aria-hidden="true"
                                                                className="h-6 w-6 text-red-600 text-error"
                                                            />
                                                        </div>
                                                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                                            <DialogTitle
                                                                as="h3"
                                                                className="text-base font-semibold text-gray-900"
                                                            >
                                                                {t('Confirm Booking')}
                                                            </DialogTitle>
                                                            <div className="mt-2">
                                                                <p className="text-sm text-gray-500">
                                                                    {t('Are you sure you want to confirm the booking?')}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                                    <button
                                                        type="button"
                                                        onClick={() => confirmBooking()}
                                                        className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm text-white font-semibold hover:bg-primary/[.9] bg-primary focus-visible:outline-primary sm:ml-3 sm:w-auto"
                                                    >
                                                        {t('Confirm')}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        data-autofocus
                                                        onClick={() => setShowConfirmBookingDialogue(false)}
                                                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                                    >
                                                        {t('Cancel')}
                                                    </button>
                                                </div>
                                            </DialogPanel>
                                        </div>
                                    </div>
                                </Dialog>
                            </div>
                        </div>
                    </div>
                </>
            )}
            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={dialogMessage} onClose={handleErrClose} />
            )}
            {submitLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-white"></div>
                </div>
            )}
            <BoatBookingConformation
                open={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                bookingData={bookingData}
                isManagePage={false}
            />
        </>
    )
}

export default BoatBookingDetail
